import React from "react";
import { formatListingDate } from "../../../utils/formatListingDate";

export const STOP_SALE_DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
export const STOP_SALE_DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

export const getStopSaleApplicableDays = (blockedDate) =>
  blockedDate?.applicable_days || blockedDate?.applicableDays || null;

export const inputClass =
  "w-full h-10 px-3 text-sm border border-gray-300 rounded-lg bg-white shadow-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/20";

export function ApplicableDayBadges({ applicableDays, compact = false }) {
  const days = applicableDays;
  if (!days) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const activeCount = STOP_SALE_DAY_KEYS.filter((key) => Boolean(days[key])).length;
  if (activeCount === 7) {
    return (
      <span className="inline-flex items-center rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold text-primary">
        Every day
      </span>
    );
  }

  return (
    <div className={`flex flex-wrap ${compact ? "gap-1" : "gap-1.5"}`}>
      {STOP_SALE_DAY_LABELS.map((label, idx) => {
        const active = Boolean(days[STOP_SALE_DAY_KEYS[idx]]);
        return (
          <span
            key={label}
            className={`inline-flex min-w-[2rem] items-center justify-center rounded-md px-1.5 py-0.5 text-[10px] font-semibold ${
              active
                ? "bg-primary/10 text-primary ring-1 ring-primary/20"
                : "bg-gray-100 text-gray-300"
            }`}
          >
            {label}
          </span>
        );
      })}
    </div>
  );
}

export function StopSaleDateRange({ start, end }) {
  const from = formatListingDate(start);
  const to = formatListingDate(end);
  const sameDay = from === to;

  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-gray-900">
      <span className="inline-flex items-center gap-1.5 font-medium">
        <i className="fi fi-rr-calendar text-primary text-xs" aria-hidden />
        {from}
      </span>
      {!sameDay ? (
        <>
          <span className="text-gray-300">→</span>
          <span className="font-medium">{to}</span>
        </>
      ) : (
        <span className="text-[11px] font-medium text-gray-500">(single day)</span>
      )}
    </div>
  );
}

export function StopSaleList({
  items,
  onDelete,
  isDeleting,
  deletingId,
  emptyHint = "No stop sales yet. Add a date range above or skip this step.",
}) {
  if (!items.length) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50/50 px-6 py-10 text-center">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-gray-100">
          <i className="fi fi-rr-ban text-lg text-gray-400" aria-hidden />
        </div>
        <p className="text-sm font-medium text-gray-700">No stop sales configured</p>
        <p className="mt-1 text-xs text-gray-500 max-w-sm mx-auto">{emptyHint}</p>
      </div>
    );
  }

  return (
    <ul className="space-y-3">
      {items.map((bd) => {
        const applicableDays = getStopSaleApplicableDays(bd);
        const isRemoving = isDeleting && deletingId === bd.id;

        return (
          <li
            key={bd.id}
            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-shadow hover:shadow-md"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0 flex-1 space-y-2">
                <StopSaleDateRange start={bd.start_datetime} end={bd.end_datetime} />
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">
                    Applicable days
                  </p>
                  <ApplicableDayBadges applicableDays={applicableDays} />
                </div>
                {bd.reason ? (
                  <p className="text-xs text-gray-600">
                    <span className="font-medium text-gray-500">Reason:</span> {bd.reason}
                  </p>
                ) : null}
              </div>
              <button
                type="button"
                onClick={() => onDelete(bd.id)}
                disabled={isRemoving}
                className="inline-flex shrink-0 items-center gap-1.5 self-start rounded-lg border border-red-100 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:opacity-50"
                title="Remove stop sale"
              >
                <i className="fi fi-rr-trash text-[11px]" aria-hidden />
                {isRemoving ? "Removing…" : "Remove"}
              </button>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function DayToggleRow({ days, onToggle, onSelectAll, onClearAll }) {
  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <label className="text-sm font-medium text-gray-700">
          Applicable days <span className="text-red-500">*</span>
        </label>
        <div className="flex items-center gap-2 text-[11px]">
          <button type="button" onClick={onSelectAll} className="font-semibold text-primary hover:underline">
            Select all
          </button>
          <span className="text-gray-300">|</span>
          <button type="button" onClick={onClearAll} className="font-semibold text-gray-500 hover:underline">
            Clear
          </button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {STOP_SALE_DAY_LABELS.map((lbl, idx) => (
          <button
            key={lbl}
            type="button"
            onClick={() => onToggle(idx)}
            className={`min-w-[2.75rem] rounded-lg px-3 py-2 text-xs font-semibold border transition-colors ${
              days[idx]
                ? "bg-primary text-white border-primary shadow-sm"
                : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
            }`}
          >
            {lbl}
          </button>
        ))}
      </div>
    </div>
  );
}
